import { Router } from "@frui.ts/screens/dist";
import type { EntityNames } from "config";
import { Modules } from "config";
import type ExemptionReason from "entities/exemptionReason";
import LicensePlate from "entities/licensePlate";
import { action, computed } from "mobx";
import EnumService from "services/enumService";
import EventBus, {
  callCenterViewModelName,
  totalCountCharges,
  totalCountExemptions,
  totalCountMessages,
  totalCountVehicleRegister,
  vehicleSearchStatus,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import CallCenterListViewModel from "../callCenterListViewModel";
import ChargesRepository from "repositories/chargesRepository";
import type SentEmailMessageSearchRequest from "entities/sentEmailMessageSearchRequest";
import type SentEmailMessage from "entities/sentEmailMessageResponse";
import EmailMessageRepository from "repositories/emailMessageRepository";
import SentEmailMessageChargeResponse from "entities/sentEmailMessageChargeResponse";
import PriceListService from "services/priceListService";
import DateRangeRequest from "entities/dateRangeRequest";
import ExemptionRepository from "repositories/exemptionRepository";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";

export const emailMessageNavigationName = CallCenterModelNamesEnum.EmailMessage;

@Router.registerRoute({ route: emailMessageNavigationName, name: Router.Self })
export default class EmailMessageViewModel extends CallCenterListViewModel<SentEmailMessage, SentEmailMessageSearchRequest> {
  protected readonly modelName: EntityNames = "emailMessage";
  countrySelectViewModel: CountrySelectViewModel;
  readonly idProperty = "searchId";

  constructor(
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected securityService: SecurityService,
    protected repository: EmailMessageRepository,
    protected chargesRepository: ChargesRepository,
    protected vehicleRegisterRepository: VehicleRegisterRepository,
    protected exemptionRepository: ExemptionRepository,
    private countrySelectVMFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = emailMessageNavigationName;
    this.countrySelectViewModel = this.countrySelectVMFactory("id");
  }

  protected onInitialize(): Promise<any> | void {
    return super.onInitialize();
  }

  protected onActivate() {
    this.eventBus.publish(callCenterViewModelName({ callCenterViewModelName: CallCenterModelNamesEnum.EmailMessage }));

    return super.onActivate();
  }

  @computed
  get licensePlate(): LicensePlate {
    return {
      licensePlate: this.appliedFilter.licensePlate ?? "",
      countryId: this.appliedFilter.countryId ?? "",
    };
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "emailMessage.name");
  }

  @computed
  get allowVehicleRehisterSearch() {
    return this.securityService.allowTo("callcenter");
  }

  @action.bound
  protected async loadItems() {
    const { countryId, licensePlate, emailAddress, orderIdent, chargeId, chargeOrderId } = this.appliedFilter;
    let sentDate: DateRangeRequest | undefined = new DateRangeRequest();

    if (this.appliedFilter?._validSince) {
      sentDate.from = new Date(this.appliedFilter._validSince);
    }
    if (this.appliedFilter?._validUntil) {
      sentDate.to = new Date(this.appliedFilter._validUntil);
    }
    if (!this.appliedFilter?._validSince && !this.appliedFilter?._validUntil) {
      sentDate = undefined;
    }

    const filter = { countryId, licensePlate, sentDate, emailAddress, orderIdent, chargeId, chargeOrderId };
    const result = await this.repository.getAll(filter, this.pagingFilter, this.abortController?.signal);
    this.publishTotalCount(result.metadata.total);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @action.bound
  protected async loadRelatedCounts() {
    const { countryId, licensePlate, officialRecordId } = this.appliedFilter;

    if (licensePlate || officialRecordId || (countryId && officialRecordId) || (countryId && licensePlate)) {
      const filter = {
        licensePlate: licensePlate,
        officialRecordId: officialRecordId,
        countryId: countryId,
      };
      const { totalCount } = await this.exemptionRepository.totalCount(filter);
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: totalCount }));
    } else {
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    }

    const chargesResponse = await this.chargesRepository.totalCount(this.appliedFilter);
    this.eventBus.publish(totalCountCharges({ totalCountCharges: chargesResponse.totalCount ?? 0 }));

    if (this.allowVehicleRehisterSearch && (licensePlate || (countryId && licensePlate))) {
      const { totalCount, searchStatus } = await this.vehicleRegisterRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: totalCount }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: searchStatus }));
    } else {
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
    }
  }

  @action.bound
  protected publishTotalCount(totalCount: number) {
    this.eventBus.publish(totalCountMessages({ totalCountMessages: totalCount }));
  }

  @action.bound
  protected resetRelatedCounts() {
    this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    this.eventBus.publish(totalCountCharges({ totalCountCharges: 0 }));
    this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
    this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
  }

  @action.bound
  private priceListItem(charge: SentEmailMessageChargeResponse) {
    return this.priceListService.priceListItemById(charge.priceListItemId);
  }

  @action.bound
  translateFuelType(charge: SentEmailMessageChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === priceItem.fuelTypeId);
    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @action.bound
  translateChargeType(charge: SentEmailMessageChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const chargeType = this.enumService.values("charge_types").find(chargeType => chargeType.id === priceItem.chargeTypeId);
    if (!chargeType) {
      return;
    }
    return this.localizationService.translateEnum("chargeValidity", chargeType.validFor);
  }

  translateSubAttribute(model: "licensePlate", attribute: keyof LicensePlate): string;
  translateSubAttribute(model: "exemptionReason" | "licensePlate", attribute: keyof LicensePlate | ExemptionReason) {
    return this.localizationService.translateAttribute(model, attribute);
  }
}
